import { Component, OnInit, OnDestroy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormControl, FormGroup, FormBuilder } from '@angular/forms';
// services
import { MasterService } from "../../services/master.service";
import { MessageService } from "../../services/message.service";
import { StoreService } from "../../services/store.service";
import * as languageLibrary from '../../services/language';
import { IntegrationConfig } from "src/app/models/integration-config.model";
import { integrationIcon } from "src/app/utils/icons";
import { SEARCH_DEBOUNCE_MILLIS } from "src/app/constants/list";
import { SecurityService } from "src/app/services/security.service";
import { IntegrationSetup } from "src/app/models/integration-setup.model";
import { IntegrationSetupSetting } from "src/app/models/integration-setup-setting.model";
import { Subscription } from 'rxjs';

@Component({
    selector: "app-integration",
    templateUrl: "./integration.component.html",
    styleUrls: ["./integration.component.scss"],
    standalone: false
})
export class IntegrationComponent implements OnInit, OnDestroy {
    /** All of the available integrations an admin can enable/disable. */
    protected integrations: IntegrationConfig[] = []

    /** The intrgrations on display in the UI based on the user search, if any. */
    protected filteredIntegrations: IntegrationConfig[] = []

    /** The currently selected lot whose intgrations the user is setting up. */
    protected lotId: number = 0;

    /** Name of the selected lot whose integration setups are being managed. */
    protected lot: string | null = null

    /** The integration setups for the currently selected lot. */
    protected setups: IntegrationSetup[] = []

    /** define if is loading */ 
    public loading: boolean = false

    // save the language
    public language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    public words = languageLibrary.language

    // The search form group
    public integrationSearch: FormGroup | null = null

// A debounce timer for search events. Used to wait for 
    // the user to stop typing before we fetch.
    private watcher: ReturnType<typeof setTimeout> = null

    private subscriptions: Subscription[] = []

    constructor(private master: MasterService, private ms: MessageService, protected store: StoreService, private router: Router, private fb: FormBuilder, protected security: SecurityService) {
        this.integrationSearch = this.fb.group({
            inquiry: new FormControl<string>('', [])
        })
    }

ngOnInit() {
        this.lotId = parseInt(localStorage.getItem('lot') || this.store.lotSelected)

        this.fetch()
        this.fetchSetups()

        this.subscriptions.push(
            this.integrationSearch.get('inquiry').valueChanges.subscribe(inquiry => {
                if (this.watcher) {
                    clearTimeout(this.watcher)
                    this.watcher = null
                }
        
                this.watcher = setTimeout(() => {
                    this.applyIntegrationFilter()
                }, SEARCH_DEBOUNCE_MILLIS)
            })
        )
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
        if (this.watcher) {
            clearTimeout(this.watcher)
        }
    }

    /**
     * Get a list of available integrations in the 
     * system and the status of each for the
     * current corporation.
     */
    private fetch = (): void => {
        this.master.get('corporation/integrations', res => {
            this.loading = false

            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.integrations = res.data.integrations || []
            this.filteredIntegrations = [].concat(this.integrations)
        })
    }

    /**
     * Get a list of available integration setups in 
     * the system and the status of each for the 
     * current lot.
     */
    private fetchSetups = (): void => {
        if (!this.lotId || (this.lotId <= 0)) {
            return
        }

        this.master.get(`lots/${this.lotId}/integrations`, res => {
            this.loading = false

            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.setups = res.data.setups || []
            this.lot = res.data.lot || null
        })
    }

    /**
     * After a user search set the filtered list of available integrations.
     */
    private applyIntegrationFilter = () => {
        const inquiry = this.integrationSearch.get('inquiry').value.trim().toLocaleLowerCase()
        if (!inquiry || (inquiry.length <= 0)) {
            this.filteredIntegrations = [].concat(this.integrations)
            return
        }

        const filtered = []
        const tokens = inquiry.replace(/[\b]+/, ' ').split(' ')
        for (const company of this.integrations) {
            const searchables: string = `${company.category.toLocaleLowerCase()} ${company.enabled ? 'enabled' : 'disabled'} ${company.name.toLocaleLowerCase()}`
            
            let matched = true
            for (const token of tokens) {
                matched = matched && searchables.includes(token)
            }

            if (!matched) {
                continue
            }

            filtered.push(company)
        }

        this.filteredIntegrations = filtered
    }

    /**
     * listen to output of navbar
    */ 
    public listenerNav = (e): void => {
        switch (e['message']) {
            case 'changeLanguage':
                this.language = e['value']
                break
            case 'changeLot':
                this.lotId = parseInt(e['lot'])
                this.fetchSetups()
                break
        }
    }

    /**
     * Given an integration company identity, toggle the enabled value.
     * @param id Unique internal identity of the integration company to toggle.
     */
    protected toggleAvailability = (id: number) => {
        if (this.security.notPermitted('corporation setup', 'corporate integrations', 'update')) {
            return
        }

        if (!id || (id <= 0) || (!this.integrations)) {
            return
        }

        const company: IntegrationConfig = this.integrations.find((item) => item.id == id)
        if (!company || !company.id) {
            return
        }

        let action = 'enable'
        if (company.enabled) {
            action = 'disable'
        }

        this.master.patch(`corporation/integrations/${id}/${action}`, null, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            company.enabled = company.enabled ? 0 : 1
            this.fetchSetups()
        })
    }

    /**
     * Listen for changes to setup settings and save them automatically.
     * @param index The index of the setup that was edited.
     * @param field The name of the setup setting field name that changed.
     * @param value The new value of the setup setting.
     */
    protected setupChanged = (index: number, field: string, value: string) => {
        if ((index < 0) || (index > (this.setups.length-1))) {
            return
        }

        const setup = this.setups[index]
        if (!setup || !setup.settings || (setup.settings.length <= 0)) {
            return
        }

        const setting = setup.settings.find((item) => item.field === field)
        if (!setting) {
            return
        }

        if (this.watcher) {
            clearTimeout(this.watcher)
            this.watcher = null
        }

        if (setting.value == value) {
            return
        }

        this.watcher = setTimeout(() => {
            setting.value = value
            this.saveSetup(setup.id, setup.settings)
        }, SEARCH_DEBOUNCE_MILLIS)
    }

    /**
     * Save the changes to the setup settings.
     * @param setupId Unique internal identity of the setup whose settings that changed.
     * @param settings The new value of the setup settings that need to be saved.
     */
    private saveSetup = (setupId: number, settings: IntegrationSetupSetting[]): void => {
        if (!this.lotId || (this.lotId <= 0)) {
            return
        }

        if (!setupId || (setupId <= 0)) {
            return
        }

        this.master.patch(`lots/${this.lotId}/integrations/${setupId}`, {settings} , res => {
            this.loading = false

            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.ms.sendMessage("alert", { type: "success", text: this.words[this.language]['saved'] })
        })
    }

    /**
     * Given an integration company category map it to 
     * a material design icon for display in the UI.
     * @param category The integration company categopry to map to an icon.
     * @returns A material icon string to use in the html
     */
    protected iconify = (category: string) => {
        return integrationIcon(category)
    }
}
